import React from "react"
import { Box, Text } from "@theme-ui/components"
import { StructuredText } from "react-datocms"
import {
  isHeading,
  isParagraph,
  isList,
  renderRule,
  isInlineItem,
} from "datocms-structured-text-utils"
import ImageGallery from "./blocks/imageGallery"
import { MagicLink } from "../utils/magicLink"
import NumbersGroup from "./blocks/numbersGroup"
import Embed from "./blocks/embed"
import DoubleImages from "./blocks/doubleImages"
import BrochureEmbed from "./blocks/brochureEmbed"
import VideoThumb from "./videoThumb"

const RichContentStructuredText = ({ text, theme }) => {
  // console.log(text)
  const componentTheme = theme || "light"
  const light = componentTheme === "light" ? "light" : "dark"
  const dark = componentTheme === "light" ? "dark" : "light"
  return (
    <Box
      sx={{
        "*:first-of-type": {
          marginTop: 0,
        },
        "*:last-child": {
          marginBottom: 0,
        },
      }}
    >
      {text.value && (
        <StructuredText
          data={text}
          renderLinkToRecord={({ record }) => {
            switch (record.__typename) {
              case "DatoCmsInternalLink":
                return <MagicLink item={record} lcoale={record.locale} />
              default:
                return null
            }
          }}
          renderInlineRecord={({ record }) => {
            switch (record.__typename) {
              case "DatoCmsInternalLink":
                return (
                  <Box
                    as="span"
                    sx={{
                      my: [7],
                      a: {
                        py: [3],
                        px: [4],
                        backgroundColor: "primary",
                        fontWeight: "normal",
                        fontSize: [2],
                        color: "light",
                        display: "inline-block",
                        textDecoration: "none",
                        border: "1px solid",
                        borderColor: "primary",
                        transition: "all .2s ease",
                        "&:hover": {
                          color: "primary",
                          backgroundColor: "transparent",
                        },
                      },
                    }}
                  >
                    <MagicLink item={record} lcoale={record.locale} />
                  </Box>
                )
              default:
                return null
            }
          }}
          renderBlock={({ record }) => {
            switch (record.__typename) {
              case "DatoCmsDoubleImage": {
                return <DoubleImages images={record.images} />
              }
              case "DatoCmsImageGallery":
                return (
                  <Box mt={5} mb={5}>
                    <ImageGallery images={record.images} key={record.id} />
                  </Box>
                )
              case "DatoCmsNumbersGroup":
                return (
                  <Box mt={5} mb={5}>
                    <NumbersGroup numbers={record.numbers} key={record.id} />
                  </Box>
                )
              case "DatoCmsEmbedVideo":
                return <VideoThumb video={record} />
              case "DatoCmsEmbed":
                return <Embed code={record.code} fullWidth={record.fullWidth} />
              case "DatoCmsBrochureEmbed":
                return <BrochureEmbed code={record.code} />
              default:
                return null
            }
          }}
          customRules={[
            renderRule(
              isInlineItem,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return ""
                  },
                  { key },
                  children
                )
              }
            ),
            renderRule(
              isHeading,
              ({ adapter: { renderNode }, node, children, key }) => {
                const slug = node.children?.[0]?.value
                  ? node.children[0].value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/(^-|-$)/g, "")
                  : "";

                return renderNode(
                  () => {
                    return (
                      <Text
                        as={`h${node.level}`}
                        variant={`h${node.level}`}
                        color={"primary"}
                        id={slug}
                        sx={{
                          scrollMarginTop: "120px",
                        }}
                      >
                        {children}
                      </Text>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
            renderRule(
              isParagraph,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <Text
                        as="p"
                        variant="p"
                        mb={3}
                        color={dark}
                        sx={{
                          lineHeight: "1.5",
                          a: { color: theme === "dark" ? "light" : "primary" },
                        }}
                      >
                        {children}
                      </Text>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
            renderRule(
              isList,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <Box my={4}>
                        <Box
                          as={node.style === "numbered" ? "ol" : "ul"}
                          sx={{
                            color: dark,
                            listStyle: node.style !== "numbered" && "none",
                            margin: 0,
                            padding: 0,
                            li: {
                              display: node.style !== "numbered" && "flex",
                              mb: 3,
                              alignItems: "flex-start",
                              borderBottom: "1px solid",
                              borderColor:
                                theme === "dark" ? "lighten" : "lightGrey",
                              pb: [3],
                              "&::before": {
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "flex-start",
                                content: node.style !== "numbered" && "'–'",
                                width: "1.5rem",
                                minWidth: "1.5rem",
                                height: "1.5rem",
                                minHeight: "1.5rem",
                                position: "relative",
                                lineHeight: "body",
                                color: theme === "dark" ? "light" : "primary",
                                borderRadius: "full",
                              },
                              p: {
                                fontSize: [2, 3],
                                mb: 0,
                                fontWeight: "300",
                              },
                            },
                          }}
                        >
                          {children}
                        </Box>
                      </Box>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
          ]}
        />
      )}
    </Box>
  )
}

export default RichContentStructuredText
